/* eslint-disable func-names */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import DeleteIcon from "@mui/icons-material/Delete";
import { Icon } from "@iconify/react";
import _ from "lodash";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { Dialog } from "@mui/material";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  formatToTitleCase,
  Error,
  HStack,
  VStack,
  getGrantStrikePrice,
} from "../../components/utils";
import {
  GrantedBy,
  useGrantDetailsStore,
} from "../../store/useGrantDetailsStore";
import { formatDisplayDate } from "../../utils/date";
import FileInput from "../../components/shared/FileInput";
import { Label, TextArea } from "../../components/shared/InputField";
import convertToBase64 from "../../utils/convertToBase64";
import { SwitchButton } from "../../components/shared/SwitchButton";
import Tooltip from "../../components/shared/Tooltip";
import {
  useAddGrant,
  useEditGrant,
  useGetEditGrantDetails,
  useGetGrantDetailsForValidation,
  useGrants,
} from "../../queries/esopPlan";
import { AddGrantReq, VestingDateType } from "../../types/Grant";
import LazyPdfDocument from "../../components/shared/LazyPdfDocument";
import { useGetAllMilestones } from "../../queries/milestone";
import { VestingMilestone } from "../../types/milestone";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import { StrikePriceType } from "../../types/EsopPlan";
import InfoBox from "../../components/shared/InfoBox";
import { Select } from "../../components/shared/Select";
import {
  useGetGrantLetterSettings,
  useGetGrantLetterTemplates,
} from "../../queries/siteSettings";
import { GrantLetterTemplate } from "../../types/SiteSettings";

interface GrantSummaryPageProps {
  onStepChange: (e: number) => void;
  onBackClick: () => void;
}

const GrantSummaryPage = (props: GrantSummaryPageProps) => {
  const currencyType = getCurrencyType();
  const currencySymbol = getCurrencySymbol();
  const data = useGrantDetailsStore();
  const navigate = useNavigate();
  const { mode, id } = useParams();
  const { mutate: createGrant, isLoading } = useAddGrant();
  const { mutate: editGrant, isLoading: editLoading } = useEditGrant();
  const { data: _grants } = useGetGrantDetailsForValidation({
    identifier: true,
    dateOfGrant: true,
  });
  const [errors, setErrors] = useState<any>({});
  const { data: _allMilestones, isFetched } = useGetAllMilestones();
  const [allMilestones, setAllMilestones] = useState<VestingMilestone[]>([]);
  const oldData = useGetEditGrantDetails(id || "");
  const { data: templates } = useGetGrantLetterTemplates();
  const { data: grantSettings } = useGetGrantLetterSettings();
  const grantLetterTemplates = useMemo(() => {
    const list: GrantLetterTemplate[] = Array.isArray(templates)
      ? templates
      : (templates as any)?.grantLetterTemplates ??
        (templates as any)?.data ??
        [];
    return list.filter((t) => Boolean(t?.entityType === "GRANT"));
  }, [templates]);
  const [regenerateGrantLetter, setRegenerateGrantLetter] = useState(false);
  const regenerateInitialized = useRef(false);
  useEffect(() => {
    setAllMilestones(_allMilestones || []);
  }, [isFetched]);
  const [expanded, setExpanded] = useState<{
    newGrant: boolean;
    grantInformation: boolean;
    vestingSchedule: boolean;
  }>({ newGrant: true, grantInformation: false, vestingSchedule: false });
  const [documentDialog, setDocumentDialog] = useState<{
    open: boolean;
    grantLetter?: string;
  }>({ open: false, grantLetter: undefined });
  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e?.target?.files?.[0]) return;
    const fileData = e.target.files[0];
    if (!fileData.name.toLowerCase().endsWith(".pdf")) return;
    const base64 = await convertToBase64(fileData);
    data.setCustomizedGrantLetter({
      file: base64 as unknown as string,
      fileName: fileData.name,
      fileType: "pdf",
      customizedLetter: true,
    });
  }
  async function handleTemplateFileUpload(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    if (!e?.target?.files?.[0]) return;
    const fileData = e.target.files[0];
    if (!fileData.name.toLowerCase().endsWith(".docx")) return;
    const base64 = await convertToBase64(fileData);
    data.setGrantLetterTemplate({
      templateName: fileData.name,
      grantLevelTemplate: base64 as string,
      planName: "",
      isAdded: true,
      isChanged: false,
      isDeleted: false,
      isPrivate: false,
    });
  }
  function handleDeleteGrantLetterTemplate() {
    data.setGrantLetterTemplate(undefined);
  }
  const grants = useMemo(() => _grants || [], [_grants]);
  function handleDeleteLetter() {
    data.setCustomizedGrantLetter({
      file: "",
      fileName: "",
      fileType: "",
      customizedLetter: false,
    });
  }
  const checkIfGrantIdExists = (value: string, mode = "add", id = "") => {
    if (grants) {
      const filteredGrants =
        mode === "edit" ? grants.filter((g) => g.id !== id) : grants;
      return filteredGrants.some((grant) => grant.identifier === value);
    } else return false;
  };

  const shouldRegenerateGrantLetter = useMemo(() => {
    const old = oldData.data;
    if (!old) return false;
    if (!data || !data.employee?.id || !data.plan?.esopPlanId) return false;

    return (
      (old.dateOfGrant && data.dateOfGrant
        ? new Date(old.dateOfGrant).setHours(0, 0, 0, 0) !==
          new Date(data.dateOfGrant).setHours(0, 0, 0, 0)
        : old.dateOfGrant !== data.dateOfGrant) ||
      (old.vestingDate && data.vestingDate
        ? new Date(old.vestingDate).setHours(0, 0, 0, 0) !==
          new Date(data.vestingDate).setHours(0, 0, 0, 0)
        : old.vestingDate !== data.vestingDate) ||
      Number(old.grantPrice) !== Number(data.grantPrice) ||
      Number(old.optionsGranted) !== Number(data.optionsGranted) ||
      (old.vestingDateType || "") !== (data.vestingDateType || "") ||
      (old.vestingTemplate?.id || "") !== (data.vestingTemplate?.id || "") ||
      (old.actualVestingDay || "") !== (data.actualVestingDay || "") ||
      (old.plan?.planName || "") !== (data.plan?.planName || "") ||
      (old.customizedGrantLetter?.customizedLetter &&
        !data.customizedGrantLetter?.customizedLetter) ||
      (old.grantLetterTemplate?.templateId || "") !==
        (data.grantLetterTemplate?.templateId || "") ||
      Boolean(data.grantLetterTemplate?.isAdded)
    );
  }, [
    oldData.data?.dateOfGrant,
    oldData.data?.grantPrice,
    oldData.data?.optionsGranted,
    oldData.data?.vestingDateType,
    oldData.data?.vestingDate,
    oldData.data?.vestingTemplate?.id,
    oldData.data?.actualVestingDay,
    oldData.data?.plan?.planName,
    oldData.data?.grantLetterTemplate?.templateId,
    data.customizedGrantLetter?.customizedLetter,
    data.dateOfGrant,
    data.grantPrice,
    data.optionsGranted,
    data.vestingDateType,
    data.vestingDate,
    data.vestingTemplate?.id,
    data.actualVestingDay,
    data.plan?.planName,
    data.employee?.id,
    data.plan?.esopPlanId,
    data.grantLetterTemplate?.templateId,
    data.grantLetterTemplate?.isAdded,
  ]);

  useEffect(() => {
    if (mode !== "edit") return;
    if (!regenerateInitialized.current) {
      setRegenerateGrantLetter(shouldRegenerateGrantLetter);
      regenerateInitialized.current = true;
      return;
    }
    setRegenerateGrantLetter(shouldRegenerateGrantLetter);
  }, [shouldRegenerateGrantLetter, mode]);
  const validationSchema = Yup.object().shape({
    employee: Yup.object().required("Name of Holder is required"),
    plan: Yup.object().required("Plan Name is required"),
    dateOfGrant: Yup.date()
      .required("Grant Date is required")
      .when("employee", (employee, schema) =>
        schema.test(
          "is-after-joining-and-plan-start",
          `Grant Date must be after the employee's joining date and the plan start date.`,
          // eslint-disable-next-line func-names
          function (value) {
            const { plan } = this.parent as {
              plan?: { planStartDate?: Date; startDate?: Date };
            };
            const employeeJoiningDate = employee?.dateOfJoin;

            const startDate = plan?.planStartDate || plan?.startDate;

            if (!value || !employeeJoiningDate || !startDate) return true;

            const grantDate = new Date(value).setHours(0, 0, 0, 0);
            const joiningDate = new Date(employeeJoiningDate).setHours(
              0,
              0,
              0,
              0
            );
            const planStartDate = new Date(startDate).setHours(0, 0, 0, 0);

            return grantDate >= joiningDate && grantDate >= planStartDate;
          } as (this: Yup.TestContext, value: Date) => boolean
        )
      ),
    grantPrice: Yup.number()

      .required("Grant Price is required")
      .min(0, "Grant Price cannot be lesser than zero"),
    grantId: Yup.string().test(
      "unique-grant-id",
      "This Grant ID already exists.",
      (value) => {
        if (!value) return true;
        const exists = checkIfGrantIdExists(value, mode, id);
        return !exists;
      }
    ),
    grantedBy: Yup.string().required("Select Granted By"),
    noOfOptions: Yup.number()
      .required("No of Options is required")
      .min(
        data?.plan?.isFractional ? 0.0001 : 1,
        `No of Options must be greater than ${
          data?.plan?.isFractional ? 0.0001 : 1
        }`
      )
      .max(
        data?.plan?.optionsReserved,
        "No. of Options must be lesser than the Options Available"
      ),
    modellingId: Yup.string().when("grantedBy", {
      is: "MODEL",
      then: Yup.string().required("Model is Required"),
      otherwise: Yup.string().notRequired(),
    }),

    selectedExistingTemplate: Yup.boolean().required(
      "No of Options is required"
    ),
    selectedVestingTemplate: Yup.string().when("selectedExistingTemplate", {
      is: true,
      then: Yup.string().required(
        "Vesting Template is required when selecting an existing template"
      ),
    }),
    vestingScheduleData: Yup.array()
      .when("selectedExistingTemplate", {
        is: false,
        then: Yup.array()
          .of(
            Yup.object().shape({
              vestingDate: Yup.string().required("Vesting Date is required"),
              percentage: Yup.number().required("Percentage is required"),
              vestingOptions: Yup.number().required(
                "Vesting Options are required"
              ),
              milestoneId: Yup.string().optional(),
            })
          )
          .required("Vesting Schedule Data is required")
          .test(
            "options-match",
            "Sum of Vesting Options must match Options Granted",
            function (vestingScheduleData) {
              const { optionsGranted, isFractional } = this.parent || {};

              if (!vestingScheduleData || !optionsGranted) return true;
              const totalVestingOptions = vestingScheduleData.reduce(
                (sum, item) => sum + (item.vestingOptions || 0),
                0
              );

              return isFractional
                ? parseFloat(totalVestingOptions.toFixed(4)) === optionsGranted
                : parseInt(totalVestingOptions.toString(), 10) ===
                    optionsGranted;
            }
          ),
      })
      .test(
        "vesting-date-check",
        "Vesting Date cannot be earlier than the Grant Date",
        function (vestingScheduleData) {
          const { dateOfGrant } = this.parent || {};

          if (!vestingScheduleData || !dateOfGrant) return true;

          const isValid = vestingScheduleData.every((item) => {
            const vestingDate = new Date(item?.vestingDate || new Date());
            const grantDateObj = new Date(dateOfGrant);

            return vestingDate >= grantDateObj;
          });

          return isValid;
        }
      ),
  });

  const formik = useFormik({
    initialValues: {},
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => {},
  });

  const validateData = async () => {
    const errors = await formik.validateForm({
      selectedExistingTemplate: data.selectedExistingTemplate,
      selectedVestingTemplate: data?.vestingTemplate?.id || "",
      optionsGranted: data?.optionsGranted,
      vestingScheduleData: data?.vestingScheduleData,
      noOfOptions: data.optionsGranted || 0,
      modellingId: data.modellingId,
      employee: data.employee,
      grantId: data.grantId,
      plan: data.plan,
      dateOfGrant: data.dateOfGrant,
      grantPrice: data.grantPrice || 0,
      grantedBy: data.grantedBy || GrantedBy.OPTIONS,
      isFractional: data?.plan?.isFractional || false,
    });
    return errors;
  };

  function handleSubmit() {
    validateData().then((errors) => {
      if (_.isEmpty(errors)) {
        if (data.employee?.id && data.plan?.esopPlanId) {
          const addReqData: AddGrantReq = {
            employeeId: data.employee.id,
            employeeName: data.employee.employeeName,
            planId: data.plan.esopPlanId,
            planName: data.plan.planName,
            planType: data.plan.planType,
            optionsGranted: data.optionsGranted,
            grantPrice: data.grantPrice || 0,
            grantType:
              data.grantType && data.grantType !== ""
                ? data.grantType
                : undefined,
            dateOfGrant: data.dateOfGrant,
            vestingDateType: data.selectedExistingTemplate
              ? data.vestingDateType
              : VestingDateType.GRANT_DATE,
            vestingTemplate: data.selectedExistingTemplate
              ? data.vestingTemplate.vestingTemplateName
              : "",
            vestingId: data.selectedExistingTemplate
              ? data.vestingTemplate.id
              : "",
            vestingDate: data.selectedExistingTemplate ? data.vestingDate : "",
            customizedGrantLetter:
              data.customizedGrantLetter.customizedLetter &&
              (data.customizedGrantLetter.file !== "" ||
                data.customizedGrantLetter.link !== ""),
            grantLetterType:
              data.customizedGrantLetter &&
              data.customizedGrantLetter.fileType !== ""
                ? data.customizedGrantLetter.fileType
                : "",
            grantLetter:
              data.customizedGrantLetter &&
              data.customizedGrantLetter.file !== ""
                ? data.customizedGrantLetter.file
                : "",
            grantLetterFileName:
              data.customizedGrantLetter &&
              data.customizedGrantLetter.fileName !== ""
                ? data.customizedGrantLetter.fileName
                : "",
            actualVestingDay: data.selectedExistingTemplate
              ? data.actualVestingDay
              : "SAME DAY",
            grantId: data.grantId || "",
            grantedBY: data.grantedBy || GrantedBy.OPTIONS,
            manuallyCreatedTemplate: !data.selectedExistingTemplate,
            templateName: data.selectedExistingTemplate
              ? ""
              : data.manualTemplateName,
            vestingScheduleData: data.selectedExistingTemplate
              ? []
              : data.vestingScheduleData,
            sharePrice: data.sharePrice,
            valuationId: data.valuationId,
            modellingId: data.modellingId,
            id,
            note: data.note,
            tags: data.tags,
            grantLetterTemplate: data.grantLetterTemplate || undefined,
          };
          if (mode === "add") {
            createGrant(addReqData, {
              onSuccess: () => {
                toast("Grant Created successfully!", {
                  type: "success",
                  autoClose: 2000,
                });
                data.clear();
                navigate("/options/allPlans");
              },
              onError: (error: any) => {
                const errorMessage = error.response.data.errorRaw as string;
                if (errorMessage.includes("Template Error")) {
                  toast(errorMessage, {
                    type: "warning",
                    autoClose: 5000,
                  });
                  navigate("/options/allPlans");
                } else {
                  toast(errorMessage, {
                    type: "error",
                    autoClose: 5000,
                  });
                }
              },
            });
          } else if (mode === "edit") {
            addReqData.regenerateGrantLetter = regenerateGrantLetter;
            editGrant(addReqData, {
              onSuccess: () => {
                toast("Grant Edited successfully!", {
                  type: "success",
                  autoClose: 5000,
                });
                data.clear();
                navigate("/options/allPlans");
              },
              onError: (error: any) => {
                toast(error.response.data.errorRaw, {
                  type: "error",
                  autoClose: 5000,
                });
                navigate("/options/allPlans");
              },
            });
          }
        }
      } else {
        setErrors(errors);
        setExpanded({
          grantInformation: true,
          newGrant: true,
          vestingSchedule: true,
        });
      }
    });
  }

  return (
    <VStack className="w-full gap-5 px-4">
      <VStack className="justify-between gap-1 pb-3 pl-2 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg1 text-black-501 ">
          Grant Summary
        </HStack>
        <HStack className="flex font-medium text-sm3 text-gray-401">
          Review the grant details and vesting schedule, and finalize the ESOP
          grant.
        </HStack>
      </VStack>
      <VStack className="flex-1 border rounded-lg shadow-box">
        <HStack
          className="items-center justify-between p-4 cursor-pointer"
          onClick={() =>
            setExpanded((prev) => ({ ...prev, newGrant: !prev.newGrant }))
          }
        >
          <VStack>
            <h5 className="text-sm font-medium text-black-501">New Grant</h5>
            {!expanded.newGrant && (
              <HStack className="gap-2 pt-1 text-gray-400 text-xxs">
                <span>
                  Employee Name: {data?.employee?.employeeName || ""} |{""}
                </span>
                <span>Plan Name: {data?.plan?.planName || ""}</span>
              </HStack>
            )}
          </VStack>
          <HStack className="items-center gap-8">
            {expanded.newGrant && (
              <ButtonPrimary1
                onClick={(e) => {
                  e.stopPropagation();
                  props.onStepChange(1);
                }}
              >
                Edit
              </ButtonPrimary1>
            )}
            <Box>
              {expanded.newGrant ? (
                <Icon icon="fe:arrow-up" width={16} />
              ) : (
                <Icon icon="fe:arrow-up" width={16} className="rotate-180" />
              )}
            </Box>
          </HStack>
        </HStack>
        {expanded.newGrant && (
          <HStack className="flex-wrap grow">
            {[
              {
                field: "Grant By",
                value: formatToTitleCase(data?.grantedBy || "OPTIONS"),
                error: errors.grantedBy,
              },
              {
                field: "Name of Holder",
                value: data.employee?.employeeName || data.employee?.name || "",
                error: errors.employeeName,
              },
              {
                field: "Grant Id",
                value: !data.grantId
                  ? "It will get Auto-Generated"
                  : data.grantId,
                error: errors.grantId,
              },
              {
                field: "Plan Name",
                value: data.plan?.planName || "",
                error: errors.planName,
              },
              {
                field: "Grant Date",
                value: data.dateOfGrant
                  ? formatDisplayDate(data.dateOfGrant)
                  : "",
                error: errors.dateOfGrant,
              },
              {
                field: "Grant Type",
                value: !data.grantType ? "ESOP" : data.grantType,
              },
            ].map(({ field, value, error }, i) => (
              <HStack key={i} className="w-1/2 gap-1 p-4">
                <span className="w-1/3 text-sm font-normal text-gray-400">
                  {field}
                </span>
                <VStack className="w-2/3">
                  <span className="text-sm font-normal ">
                    {value?.toString()}
                  </span>
                  {error && <Error text={error} />}
                </VStack>
              </HStack>
            ))}
          </HStack>
        )}
      </VStack>
      <VStack className="flex-1 border rounded-lg shadow-box">
        <HStack
          className="items-center justify-between p-4 cursor-pointer"
          onClick={() =>
            setExpanded((prev) => ({
              ...prev,
              grantInformation: !prev.grantInformation,
            }))
          }
        >
          <VStack>
            <h5 className="text-sm font-medium text-black-501">
              Grant Information
            </h5>
            {!expanded.grantInformation && (
              <HStack className="gap-2 pt-1 text-gray-400 text-xxs">
                <span>No of Options: {data?.optionsGranted || ""}</span>|{" "}
                {!expanded.grantInformation && (
                  <span>
                    Cost of Grant:{" "}
                    {formatCurrency(
                      data.optionsGranted *
                        getGrantStrikePrice(
                          data?.grantPrice || 0,
                          data.plan?.conversionShares || 1,
                          data.plan?.strikePriceType ||
                            StrikePriceType.OPTIONS_PRICE
                        ) || 0,
                      currencyType
                    )}
                  </span>
                )}
                |{" "}
                {!expanded.grantInformation && (
                  <span>
                    Value At Grant:{" "}
                    {formatCurrency(
                      data.optionsGranted *
                        data.sharePrice *
                        (data.plan?.conversionShares || 1),
                      currencyType
                    ) || ""}
                  </span>
                )}
              </HStack>
            )}
          </VStack>
          <HStack className="items-center gap-8">
            {expanded.grantInformation && (
              <ButtonPrimary1
                onClick={(e) => {
                  e.stopPropagation();
                  props.onStepChange(2);
                }}
              >
                Edit
              </ButtonPrimary1>
            )}
            <Box>
              {expanded.grantInformation ? (
                <Icon icon="fe:arrow-up" width={16} />
              ) : (
                <Icon icon="fe:arrow-up" width={16} className="rotate-180" />
              )}
            </Box>
          </HStack>
        </HStack>
        {expanded.grantInformation && (
          <HStack className="flex-wrap grow">
            {[
              {
                field: "No of Options",
                value: data.optionsGranted.toLocaleString(currencyType, {
                  maximumFractionDigits: 4,
                }),
                error: errors.noOfOptions,
              },
              {
                field: "Share Price",
                value: formatCurrency(data.sharePrice, currencyType),
              },
              {
                field: "Value At Grant",
                value:
                  data.grantedBy === GrantedBy.AMOUNT
                    ? formatCurrency(
                        data.optionsGranted *
                          data.sharePrice *
                          (data.plan?.conversionShares || 1),
                        currencyType
                      )
                    : formatCurrency(data.valueOfOptions, currencyType),
              },
              {
                field: "Cost of Grant",
                value: formatCurrency(
                  data.optionsGranted *
                    getGrantStrikePrice(
                      data?.grantPrice || 0,
                      data.plan?.conversionShares || 1,
                      data.plan?.strikePriceType ||
                        StrikePriceType.OPTIONS_PRICE
                    ),
                  currencyType
                ),
              },
              {
                field: "Grant Price",
                value: formatCurrency(data.grantPrice, currencyType),
                error: errors.grantPrice,
              },
            ].map(({ field, value, error }, i) => (
              <HStack key={i} className="w-1/2 gap-1 p-4">
                <span className="w-1/3 text-sm font-normal text-gray-400">
                  <HStack className="gap-1">
                    {field}
                    {field === "Value At Grant" && (
                      <Tooltip
                        text={`Calculated as No. of Options × Share Price × Conversion Ratio (if any).`}
                        _className="w-96"
                      >
                        <Icon
                          icon="material-symbols:info-outline-rounded"
                          height={18}
                          width={18}
                          className="w-full text-xs font-medium rounded cursor-pointer text-slate-dark items-center"
                        />
                      </Tooltip>
                    )}
                  </HStack>
                </span>
                <VStack className="w-2/3">
                  <span className="text-sm font-normal ">
                    {value?.toString()}
                  </span>
                  {error && <Error text={error} />}
                </VStack>
              </HStack>
            ))}
          </HStack>
        )}
      </VStack>
      <VStack className="flex-1 border rounded-lg shadow-box">
        <HStack
          className="items-center justify-between p-4 cursor-pointer"
          onClick={() =>
            setExpanded((prev) => ({
              ...prev,
              vestingSchedule: !prev.vestingSchedule,
            }))
          }
        >
          <VStack>
            <h5 className="text-sm font-medium text-black-501">
              Vesting Schedule
            </h5>
            {!expanded.vestingSchedule && (
              <HStack className="gap-2 pt-1 text-gray-400 text-xxs">
                {data.selectedExistingTemplate ? (
                  <span>
                    Template Name:{" "}
                    {data?.vestingTemplate?.vestingTemplateName || ""}
                  </span>
                ) : (
                  <span>
                    Template Name: {data?.manualTemplateName || "Manual"}
                  </span>
                )}
              </HStack>
            )}
          </VStack>
          <HStack className="items-center gap-8">
            {expanded.vestingSchedule && (
              <ButtonPrimary1
                onClick={(e) => {
                  e.stopPropagation();
                  props.onStepChange(3);
                }}
              >
                Edit
              </ButtonPrimary1>
            )}
            <Box>
              {expanded.vestingSchedule ? (
                <Icon icon="fe:arrow-up" width={16} />
              ) : (
                <Icon icon="fe:arrow-up" width={16} className="rotate-180" />
              )}
            </Box>
          </HStack>
        </HStack>
        <Dialog
          open={documentDialog.open}
          onClose={() => setDocumentDialog({ open: false })}
          maxWidth="lg"
        >
          <LazyPdfDocument url={documentDialog.grantLetter ?? ""} />
        </Dialog>
        {expanded.vestingSchedule && (
          <>
            <HStack className="flex-wrap grow">
              {[
                ...(data.selectedExistingTemplate
                  ? [
                      {
                        field: "Vesting Schedule",
                        value: data?.vestingTemplate?.vestingTemplateName || "",
                        error: errors.selectedVestingTemplate,
                      },
                      {
                        field: "Vesting Date Type",
                        value: formatToTitleCase(
                          (
                            data?.vestingDateType || VestingDateType.GRANT_DATE
                          ).replace("_", " ")
                        ),
                      },
                      {
                        field: "Actual Vesting Day",
                        value: formatToTitleCase(
                          (data?.actualVestingDay || "SAME_DAY").replace(
                            "_",
                            " "
                          )
                        ),
                      },
                      ...(data.vestingDateType &&
                      data.vestingDateType === VestingDateType.CUSTOM_DATE
                        ? [
                            {
                              field: "Vesting Start Date",
                              value: data?.vestingDate
                                ? formatDisplayDate(data?.vestingDate)
                                : "",
                            },
                          ]
                        : []),
                    ]
                  : [
                      {
                        field: "Template Name",
                        value: data?.manualTemplateName || "",
                      },
                    ]),
              ].map(({ field, value, error }, i) => (
                <HStack key={i} className="w-1/2 gap-1 p-4">
                  <span className="w-1/3 text-sm font-normal text-gray-400">
                    {field}
                  </span>
                  <VStack className="w-2/3">
                    <span className="text-sm font-normal">{value}</span>
                    {error && <Error text={error} />}
                  </VStack>
                </HStack>
              ))}
            </HStack>
            {!data.selectedExistingTemplate && (
              <HStack className="pl-3">
                <table className="w-full table-space">
                  <thead className="text-xxs font-normal text-[#B8B7C9]">
                    <tr className="border-b border-gray-200 border-dashed">
                      <td className="px-2 py-2 align-top">Vesting Date</td>
                      <td className="px-2 py-2 align-top">No. Of options</td>
                      {data.vestingScheduleData &&
                        data.vestingScheduleData.some((v) => v.milestoneId) && (
                          <td className="px-2 py-2 align-top">
                            Milestone Name
                          </td>
                        )}
                    </tr>
                  </thead>
                  <tbody className="text-[#4E4E4E] ">
                    {data.vestingScheduleData.map((vesting, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-200 border-dashed "
                      >
                        <td className="px-2 py-2 align-top ">
                          <HStack>
                            <div className="mr-2 whitespace-nowrap">
                              {formatDisplayDate(vesting.vestingDate)}
                            </div>
                          </HStack>
                        </td>
                        <td className="px-2 py-2 align-top ">
                          <HStack>
                            <div className="mr-2 whitespace-nowrap">
                              {vesting.vestingOptions.toLocaleString(
                                currencyType,
                                {
                                  maximumFractionDigits: 4,
                                }
                              )}
                            </div>
                          </HStack>
                        </td>
                        {data.vestingScheduleData &&
                          data.vestingScheduleData.some(
                            (v) => v.milestoneId
                          ) && (
                            <td className="px-2 py-2 align-top ">
                              <HStack>
                                <div className="mr-2 whitespace-nowrap">
                                  {allMilestones.find(
                                    (m) => m.id === vesting.milestoneId
                                  )?.milestoneName || ""}
                                </div>
                              </HStack>
                            </td>
                          )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </HStack>
            )}
          </>
        )}
      </VStack>
      <HStack>
        <div className="flex-1 pt-4 pl-2">
          <HStack className="gap-2 items-center mb-2">
            <Label className="text-sm font-medium text-black-501 ">
              Grant Letter Template
            </Label>
            <Tooltip
              text={`Choose a pre-configured template from the dropdown, or upload a grant-specific .docx file to override it. The selected template will be used to generate the grant letter for this grant.`}
              _className="w-96"
            >
              <Icon
                icon="material-symbols:info-outline-rounded"
                height={18}
                width={18}
                className="w-full text-xs font-medium rounded cursor-pointer text-slate-dark items-center mb-2"
              />
            </Tooltip>
          </HStack>
          <HStack className="gap-4 items-center mb-6">
            <div className="w-48 shrink-0">
              <Select
                placeholder="Select a template"
                value={
                  !data.grantLetterTemplate?.isAdded
                    ? data.grantLetterTemplate?.templateId || ""
                    : ""
                }
                options={grantLetterTemplates}
                valueGetter={(o) => o.templateId || ""}
                textGetter={(o) => o.planName || o.templateName || ""}
                onChange={(e) => {
                  const template = grantLetterTemplates.find(
                    (t) => (t.templateId || "") === e.target.value
                  );
                  data.setGrantLetterTemplate(template || undefined);
                }}
              />
            </div>
            <span className="text-sm font-medium text-gray-400">or</span>
            <div className="flex items-center gap-2 min-w-0">
              <FileInput
                id="grant-letter-template"
                accept=".docx"
                file={
                  data.grantLetterTemplate?.isAdded
                    ? data.grantLetterTemplate.templateName || "Upload Template"
                    : "Upload Template"
                }
                onChange={handleTemplateFileUpload}
                className="max-w-[200px] truncate"
              />
              <button
                onClick={handleDeleteGrantLetterTemplate}
                className="ml-2 text-zinc-300 hover:scale-105"
              >
                <DeleteIcon />
              </button>
            </div>
          </HStack>
          {grantSettings?.disableAutoGrantLetterGeneration && (
            <VStack className="p-4 text-black-501 bg-yellow-100 rounded">
              <HStack className="pt-0 justify-start">
                <HStack className="gap-1 pl-1 items-start">
                  <Icon
                    icon="material-symbols:info-outline-rounded"
                    height={18}
                    width={18}
                    className="w-full text-xs font-medium text-black-501 rounded"
                  />
                  <p className="text-sm">Note</p>
                  <p> : </p>
                  <p className="text-xs pt-0.5 whitespace-nowrap">
                    Auto grant letter generation is currently turned off. Grant
                    letters will not be generated automatically upon grant
                    creation.
                  </p>
                </HStack>
              </HStack>
            </VStack>
          )}
          <HStack className="gap-2 items-center pb-1">
            <Label className="text-sm font-medium text-black-501">
              Upload Custom Grant Letter
            </Label>
            <Tooltip
              text={`Upload a finalised grant letter (PDF) to attach directly to this grant. This will bypass template generation and use the uploaded file as-is.`}
              _className="w-96"
            >
              <Icon
                icon="material-symbols:info-outline-rounded"
                height={18}
                width={18}
                className="w-full text-xs font-medium rounded cursor-pointer text-slate-dark items-center mb-2"
              />
            </Tooltip>
          </HStack>
          <div className="flex flex-row items-center pl-2 cursor-pointer align-center">
            {data.customizedGrantLetter.customizedLetter && mode !== "add" ? (
              <>
                <div className="flex flex-row items-center justify-between cursor-pointer align-center">
                  {data.customizedGrantLetter.link ? (
                    <div
                      className="text-red-500 underline "
                      onClick={() =>
                        setDocumentDialog({
                          open: true,
                          grantLetter: data.customizedGrantLetter.link,
                        })
                      }
                    >
                      Grant Letter.pdf
                    </div>
                  ) : (
                    <FileInput
                      id="custom-grant-letter-replace"
                      accept=".pdf"
                      file={data.customizedGrantLetter.fileName || "Replace"}
                      onChange={(e) => {
                        handleFileUpload(e);
                      }}
                    />
                  )}
                </div>
              </>
            ) : (
              <FileInput
                id="custom-grant-letter"
                accept=".pdf"
                file={data.customizedGrantLetter?.fileName || "Attach File"}
                onChange={(e) => {
                  handleFileUpload(e);
                }}
              />
            )}
            <button
              onClick={() => handleDeleteLetter()}
              className="ml-6 text-zinc-300 hover:scale-105"
            >
              <DeleteIcon />
            </button>
          </div>
        </div>
        <div className="flex-1"></div>
      </HStack>
      <HStack>
        <div className="flex-1 pt-4 pl-2">
          <Label className="text-sm font-normal">Notes</Label>
          <TextArea
            value={data?.note || ""}
            onChange={(e) => data.setNote(e.target.value)}
          />
        </div>
      </HStack>
      {mode === "edit" && (
        <div className="gap-6">
          <HStack className="w-full pt-4">
            <div className="flex gap-2">
              <div className="flex items-center w-18">
                <SwitchButton
                  value={regenerateGrantLetter}
                  label={""}
                  onClick={() =>
                    setRegenerateGrantLetter(!regenerateGrantLetter)
                  }
                  className="mr-2"
                />
              </div>
              <div className="flex flex-col">
                <div className="font-medium">Regenerate Grant Letter</div>
                <div className="text-gray-500 text-xs">
                  Toggle to regenerate the grant letter for the edited grant.
                </div>
              </div>
            </div>
          </HStack>
          <HStack className="mt-2">
            {!shouldRegenerateGrantLetter && regenerateGrantLetter && (
              <InfoBox
                message={
                  "No material changes were detected in this grant. The grant letter will still be regenerated because the 'Regenerate Grant Letter' toggle is enabled. Disable the toggle if you do not want a new letter."
                }
              />
            )}

            {shouldRegenerateGrantLetter && !regenerateGrantLetter && (
              <InfoBox
                message={
                  "Changes were detected that would normally trigger a new grant letter, but regeneration is disabled. Enable the 'Regenerate Grant Letter' toggle to generate an updated letter."
                }
              />
            )}
          </HStack>
        </div>
      )}
      <HStack className="justify-between pt-4">
        <ButtonPrimary1 onClick={props.onBackClick}>Back</ButtonPrimary1>
        <ButtonPrimary
          onClick={() => handleSubmit()}
          loading={isLoading || editLoading}
        >
          Save Grant
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
};

export default GrantSummaryPage;
